import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, XCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import apiClient from '../services/apiClient';

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 1500;

// Handles the redirect back from Stripe Checkout for a credit-pack
// purchase (?success=true|false). Polls the credits endpoint directly
// (bypassing Publications.jsx's cached fetchImageCredits, whose 10-minute
// TTL would otherwise show the pre-purchase balance) until the webhook
// fulfillment lands, then hands off to Publications.jsx with a
// forceRefreshCredits flag so its badge picks up the fresh balance.
//
// The baseline balance to compare against comes from sessionStorage
// (stashed by BuyCreditsModal right before the Stripe redirect) — the
// webhook that credits a purchase routinely fires in well under a second,
// often before the browser makes it back from Stripe's hosted checkout, so
// capturing "before" from our own first poll here would already see the
// post-purchase value and never detect an increase.
const CreditsReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === 'true';
  const [attempts, setAttempts] = useState(0);
  const [baseline] = useState(() => {
    const stored = sessionStorage.getItem('credit_purchase_baseline');
    sessionStorage.removeItem('credit_purchase_baseline');
    return stored !== null ? Number(stored) : null;
  });
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!success) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await apiClient.get('/api/publications/credits');
        const balance = data.purchased_balance || 0;
        if (cancelled) return;

        // No baseline on file (e.g. this page was reloaded directly) — fall
        // back to just confirming a response came back at all.
        if (baseline === null || balance > baseline) {
          setConfirmed(true);
          return;
        }
      } catch {
        // keep polling — a transient error here shouldn't block the flow
      }
      if (!cancelled) setAttempts((a) => a + 1);
    };

    if (attempts >= MAX_POLL_ATTEMPTS) return;
    const timer = setTimeout(poll, attempts === 0 ? 0 : POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [success, attempts, baseline]);

  const goToPublications = () => navigate('/publications', { replace: true, state: { forceRefreshCredits: true } });

  if (!success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Pago cancelado</h1>
          <p className="text-muted-foreground mb-6">No se completó la compra de créditos. Puedes intentarlo de nuevo cuando quieras.</p>
          <Button onClick={goToPublications} className="bg-blue-600 hover:bg-blue-500 text-white">
            Volver a Publicaciones
          </Button>
        </div>
      </div>
    );
  }

  if (confirmed || attempts >= MAX_POLL_ATTEMPTS) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmed ? 'bg-emerald-600/20' : 'bg-blue-600/20'}`}>
            {confirmed
              ? <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              : <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />}
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {confirmed ? '¡Créditos agregados!' : 'Estamos confirmando tu pago'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {confirmed
              ? 'Tu saldo de créditos ya está actualizado.'
              : 'Esto está tomando más de lo usual. Si tu pago fue exitoso, tus créditos se acreditarán en breve.'}
          </p>
          <Button onClick={goToPublications} className="bg-blue-600 hover:bg-blue-500 text-white">
            Volver a Publicaciones
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Confirmando tu pago...</h1>
        <p className="text-muted-foreground">Esto solo toma unos segundos</p>
      </div>
    </div>
  );
};

export default CreditsReturn;
